import { createFileRoute, Link, notFound, Outlet, useLocation } from "@tanstack/react-router";
import { ArrowUpRight } from "lucide-react";
import { getLocation, locationSlugs } from "@/content/locations";
import { MarkdownBody, prepareMarkdownBodyForPage } from "@/components/MarkdownBody";
import { ContactForm } from "@/components/ContactForm";
import { PageHero } from "@/components/PageHero";
import { CtaBand } from "@/components/sections/CtaBand";
import { RelatedNews } from "@/components/sections/RelatedNews";
import type { PageContent } from "@/content/types";
import { breadcrumbSchema, pageMetaTitle, metaDescription, ORGANIZATION_ID, SITE_URL } from "@/lib/seo";

function stripFormNoise(body: string): string {
  const markers = [
    "## Get in touch",
    "Select a serviceSEO",
    "We use cookies to improve your experience",
  ];
  let cleaned = body;
  for (const m of markers) {
    const idx = cleaned.indexOf(m);
    if (idx !== -1) cleaned = cleaned.slice(0, idx).trimEnd();
  }
  return cleaned;
}

function stripFirstHeading(body: string): string {
  return body.replace(/^#\s+.+\n+/, "");
}

export const Route = createFileRoute("/seo/$location")({
  loader: ({ params }) => {
    const data = getLocation(params.location);
    if (!data) throw notFound();
    return data;
  },
  head: ({ loaderData, matches }) => {
    const deepestMatch = matches[matches.length - 1];
    const isLocationHub = deepestMatch?.routeId === "/seo/$location";

    if (!loaderData || !isLocationHub) {
      return { meta: [], links: [] };
    }

    const url = `https://www.phil-carr.co.uk/seo/${loaderData.hub.slug}`;
    const description = metaDescription(loaderData.hub.description);
    const locationName = loaderData.hub.slug.charAt(0).toUpperCase() + loaderData.hub.slug.slice(1);
    return {
      meta: [
        { title: pageMetaTitle(`/seo/${loaderData.hub.slug}`, loaderData.hub.title) },
        { name: "description", content: description },
        { property: "og:title", content: pageMetaTitle(`/seo/${loaderData.hub.slug}`, loaderData.hub.title) },
        { property: "og:description", content: description },
        { property: "og:url", content: url },
      ],
      links: [{ rel: "canonical", href: url }],
      scripts: [
        {
          type: "application/ld+json",
          children: JSON.stringify(
            breadcrumbSchema([
              { name: "Home", url: `${SITE_URL}/` },
              { name: "SEO", url: `${SITE_URL}/seo` },
              { name: loaderData.hub.title.replace(/\s*[—|].*$/, ""), url },
            ]),
          ),
        },
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Service",
            name: `SEO services in ${locationName}`,
            description,
            serviceType: [
              "Local SEO",
              "Technical SEO",
              "Content marketing",
              "SEO copywriting",
              "Google Business Profile optimisation",
              "Digital PR and link building",
            ],
            provider: { "@id": ORGANIZATION_ID },
            areaServed: [
              locationName,
              loaderData.hub.slug === "barnsley" ? "South Yorkshire" : "Yorkshire",
            ],
          }),
        },
      ],
    };
  },
  component: LocationHubPage,
  notFoundComponent: () => (
    <div className="container mx-auto max-w-3xl px-6 py-24 text-center">
      <h1 className="font-display text-4xl">Location not found</h1>
      <p className="mt-4 text-muted-foreground">
        I cover {locationSlugs.length} cities across Yorkshire — try one of the links below.
      </p>
      <ul className="mt-6 flex flex-wrap justify-center gap-3">
        {locationSlugs.map((s) => (
          <li key={s}>
            <Link
              to="/seo/$location"
              params={{ location: s }}
              className="rounded-full border border-border px-4 py-2 text-sm capitalize hover:bg-secondary"
            >
              {s}
            </Link>
          </li>
        ))}
      </ul>
    </div>
  ),
  errorComponent: ({ error }) => (
    <div className="container mx-auto max-w-3xl px-6 py-24 text-center">
      <h1 className="font-display text-3xl">Something went wrong</h1>
      <p className="mt-3 text-muted-foreground">{error.message}</p>
    </div>
  ),
});

function LocationHubPage() {
  const currentLocation = useLocation();
  const data = Route.useLoaderData();
  const { location } = Route.useParams();
  const topics = Object.entries(data.topics) as Array<[string, PageContent | undefined]>;
  const pageTitle = data.hub.body.match(/^#\s+(.+)$/m)?.[1]?.trim() ?? data.hub.title;
  const preparedBody = prepareMarkdownBodyForPage(stripFirstHeading(stripFormNoise(data.hub.body)));

  if (currentLocation.pathname !== `/seo/${location}`) {
    return <Outlet />;
  }

  return (
    <>
      <PageHero title={pageTitle} backgroundImage={preparedBody.leadImage?.src} />
      <article className="inner-content-section container mx-auto max-w-5xl px-6 py-16 sm:px-8 lg:px-10 lg:py-24">
        <MarkdownBody>{preparedBody.body}</MarkdownBody>
        {topics.length > 0 && (
          <section className="demo-related-section">
            <h2 className="font-display text-3xl">More about SEO in {location}</h2>
            <ul className="demo-related-grid demo-related-grid-2">
              {topics.map(([topic, page]) => {
                if (!page) return null;
                return (
                  <li key={topic}>
                    <Link
                      to="/seo/$location/$topic"
                      params={{ location, topic }}
                      className="demo-card demo-card-hover demo-related-card group"
                    >
                      <span>
                        <span className="demo-related-card-title text-lg">{page.title}</span>
                        <span className="demo-related-card-text">{page.description}</span>
                      </span>
                      <span className="demo-arrow-circle demo-arrow-small mt-5">
                        <ArrowUpRight className="h-4 w-4 shrink-0 transition-transform group-hover:rotate-45" />
                      </span>
                    </Link>
                  </li>
                );
              })}
            </ul>
          </section>
        )}
      </article>
      <RelatedNews
        slugs={[
          "seo-in-2026-how-local-yorkshire-businesses-can-win-more-customers-from-google",
          "local-seo-and-the-consumer-journey",
          "local-search-in-2025-personalisation-and-contextual-relevance",
        ]}
      />
      <CtaBand />
      <section className="inner-contact-section border-t border-border bg-background">
        <div className="container mx-auto max-w-3xl px-6 py-16 sm:px-8 lg:px-10 lg:py-24">
          <p className="demo-section-label">Get in touch</p>
          <h2 className="site-heading-lg mt-4 font-display font-light capitalize">
            Talk about SEO in <em>{location}</em>
          </h2>
          <p className="site-copy mt-6 text-muted-foreground">
            Tell me about your business and your goals. I'll come back with a clear view on what's
            worth doing first.
          </p>
          <div className="mt-10">
            <ContactForm pageContext={data.hub.title} />
          </div>
        </div>
      </section>
    </>
  );
}
